import { Maximize, Minimize, ZoomIn, ZoomOut } from 'lucide-react'
import Image from 'next/image'
import * as React from 'react'

import { cn } from '@/lib/utils'

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

import { useFullscreen, useImageZoom } from '../hooks'

import type { ResourceType } from '@/types/resource.type'

export function ImageContentViewer({ resource, className }: { resource: ResourceType; className?: string }) {
  const imgContainerRef = React.useRef<HTMLDivElement>(null)
  const [isLoaded, setIsLoaded] = React.useState(false)
  const { isFullscreen, toggleFullscreen } = useFullscreen(imgContainerRef)
  const zoom = useImageZoom()

  return (
    <div className={cn('flex flex-col h-screen', className)}>
      <div
        ref={imgContainerRef}
        className="relative flex-1 w-full min-h-0 overflow-hidden flex items-center justify-center"
        style={{ cursor: 'pointer' }}
        onClick={toggleFullscreen}
      >
        {!isLoaded && <Skeleton className="absolute inset-0 w-full h-full rounded-none" />}
        <div
          className="relative"
          style={{
            transform: `translate(${zoom.position.x}px, ${zoom.position.y}px) scale(${zoom.zoomLevel})`,
            transformOrigin: 'center',
            cursor: zoom.zoomLevel > 1 ? (zoom.isDragging ? 'grabbing' : 'grab') : 'pointer',
          }}
          onMouseDown={zoom.handleMouseDown}
          onMouseMove={zoom.handleMouseMove}
          onMouseUp={zoom.handleMouseUpOrLeave}
          onMouseLeave={zoom.handleMouseUpOrLeave}
        >
          <Image
            src={resource.url || '/placeholder.svg'}
            alt={resource.fileName as string}
            width={5000}
            height={5000}
            className="w-full h-full object-contain"
            // Large diagrams/screenshots often exceed Next.js optimizer's 9000px source limit.
            unoptimized
            onLoad={() => setIsLoaded(true)}
          />
        </div>
      </div>
      <div className="flex-shrink-0 bg-white border-t py-2 flex items-center justify-center space-x-2 md:space-x-4">
        <Button variant="secondary" size="icon" aria-label="Zoom In" onClick={zoom.handleZoomIn}>
          <ZoomIn className="size-4" />
        </Button>
        <Button
          variant="secondary"
          size="icon"
          aria-label="Reset Zoom"
          onClick={zoom.resetZoom}
          disabled={zoom.zoomLevel === 1}
        >
          <span className="text-xs">1:1</span>
        </Button>
        <Button
          variant="secondary"
          size="icon"
          aria-label="Zoom Out"
          onClick={zoom.handleZoomOut}
          disabled={zoom.zoomLevel <= 0.5}
        >
          <ZoomOut className="size-4" />
        </Button>
        <Button variant="secondary" size="icon" aria-label="Toggle Fullscreen" onClick={toggleFullscreen}>
          {isFullscreen ? <Minimize className="size-4" /> : <Maximize className="size-4" />}
        </Button>
        <span className="ml-4 text-sm font-medium">Zoom: {Math.round(zoom.zoomLevel * 100)}%</span>
      </div>
    </div>
  )
}
